import React, { useEffect, useState } from "react";

import { format } from "date-fns";
import _ from "lodash";

import { FormulaEditor } from "shared/components/vega/ExpressionEngine";
import { useApplicantWorkflowMutations } from "shared/hooks/api";
import { useConfirmation } from "shared/hooks/useConfirmation";

import BasicEditor from "./BasicEditor";
import V1MigrationPreview from "./V1MigrationPreview";

interface ApplicantWorkflowEditorProps {
  workflow: any;
  canEdit?: boolean;
  selectedVersion?: any;
  stagedFormula?: string;
  onRevertStaged?: () => void;
}

const ApplicantWorkflowEditor = ({
  workflow,
  canEdit = false,
  selectedVersion = null,
  stagedFormula,
  onRevertStaged,
}: ApplicantWorkflowEditorProps) => {
  const [resolvedWorkflow, setWorkflow] = useState(workflow);
  const [expressionFormula, setExpressionFormula] = useState(
    selectedVersion?.expression?.formula ||
      selectedVersion?.formula ||
      resolvedWorkflow?.expression?.formula
  );

  const { updateApplicantWorkflow, deleteApplicantWorkflow } =
    useApplicantWorkflowMutations();
  const { confirm } = useConfirmation();

  // Store the original formula for comparison during V1 migration preview
  const originalFormula = resolvedWorkflow?.expression?.formula;

  // Sync resolvedWorkflow when the workflow prop changes (e.g., after query refetch)
  useEffect(() => {
    setWorkflow(workflow);
  }, [workflow]);

  // Handle staged formula from V1 migration (already validated/formatted)
  useEffect(() => {
    if (stagedFormula) {
      setExpressionFormula(stagedFormula);
    }
  }, [stagedFormula]);

  // Sync editor when selectedVersion or workflow changes
  useEffect(() => {
    if (selectedVersion) {
      if (selectedVersion.expression?.formula || selectedVersion.formula) {
        setExpressionFormula(
          selectedVersion.expression?.formula || selectedVersion.formula
        );
      } else {
        setExpressionFormula(
          `// Version from ${new Date(
            parseInt(selectedVersion.timestamp, 10)
          ).toLocaleString()} - Expression data not available`
        );
      }
    } else if (!stagedFormula) {
      setExpressionFormula(resolvedWorkflow?.expression?.formula);
    }
  }, [selectedVersion, resolvedWorkflow, stagedFormula]);

  const canSave =
    canEdit &&
    !selectedVersion &&
    _.some([resolvedWorkflow?.expression?.formula !== expressionFormula]);

  const applyReset = () => {
    if (stagedFormula && onRevertStaged) {
      onRevertStaged();
    }
    setExpressionFormula(resolvedWorkflow?.expression?.formula);
  };

  const onReset = () => {
    if (canSave) {
      confirm(
        {
          title: 'Discard changes?',
          message: 'Your unsaved changes will be lost.',
          submitButtonLabel: 'Discard changes',
          cancelLinkLabel: 'Keep editing',
        },
        applyReset
      );
    } else {
      applyReset();
    }
  };

  const onDelete = () => {
    confirm(
      {
        title: 'Delete workflow?',
        message: 'This workflow will be permanently deleted. This action cannot be undone.',
        submitButtonLabel: 'Delete workflow',
        cancelLinkLabel: 'Cancel',
      },
      () => deleteApplicantWorkflow(resolvedWorkflow?.id)
    );
  };

  const onSave = async () => {
    setWorkflow(
      await updateApplicantWorkflow({
        id: resolvedWorkflow?.id,
        data: {
          formula: expressionFormula,
        },
      })
    );
  };

  // V1 Migration: Show side-by-side comparison when staged formula is present
  if (stagedFormula) {
    // isEdited means the user has modified the preview from the original staged formula
    const isEdited = expressionFormula !== stagedFormula;

    const onRestorePreview = () => {
      setExpressionFormula(stagedFormula);
    };

    return (
      <V1MigrationPreview
        originalFormula={originalFormula}
        previewFormula={expressionFormula}
        canEdit={canEdit && !selectedVersion}
        canSave={canSave}
        isEdited={isEdited}
        onChange={setExpressionFormula}
        onReset={onReset}
        onSave={onSave}
        onDelete={onDelete}
        onRestorePreview={onRestorePreview}
      />
    );
  }

  return (
    <div className="vega flex no-wrap no-gap applicant-workflow workflow-editor">
      <BasicEditor
        formula={expressionFormula}
        canEdit={canEdit && !selectedVersion}
        onChange={setExpressionFormula}
      />

      <FormulaEditor
        formula={expressionFormula}
        canSave={canSave}
        canEdit={canEdit && !selectedVersion}
        onChange={setExpressionFormula}
        onReset={onReset}
        onSave={onSave}
        onDelete={onDelete}
        readOnlyFormat={
          !selectedVersion || !!selectedVersion.expression?.formula
        }
        versionNotice={
          selectedVersion && (
            <div className="vega version-notice">
              Viewing version from{" "}
              {selectedVersion.timestamp
                ? format(
                    new Date(parseInt(selectedVersion.timestamp, 10)),
                    "MMM dd, yyyy h:mm a"
                  )
                : "unknown time"}
            </div>
          )
        }
      />
    </div>
  );
};

export default ApplicantWorkflowEditor;
